import type { ReactNode } from 'react';
import type { FieldValues, Path } from 'react-hook-form';
import { DESCRIPTION_MAX, type PhraseLibrary } from '@ast-data/shared';
import RichTextArea, { type FindingRefSupport } from '../RichTextArea';
import type { FormFieldBinding } from './binding';

export interface RichTextFieldOptions {
    /** Bare icon element left of the label — `icon={<NotesIcon />}`. */
    icon?: ReactNode;
    /** Rows shown inline; the fullscreen editor is always tall. */
    minRows?: number;
    /** Textbausteine for this field, if it has a phrase library. */
    library?: PhraseLibrary;
    /** Mark the Befunde the text names — opt-in, see RichTextArea. */
    findingRefs?: FindingRefSupport;
    /** The character ceiling, when the field's is not DESCRIPTION_MAX. */
    maxLength?: number;
}

/**
 * A long-text field (Textbausteine, Diktat, counter, fullscreen). Every record
 * form spelled the same four props out at each of its textareas — control,
 * the label off the catalog, the character ceiling, and whether the field is
 * soft-required; only the icon and the height are ever the field's own.
 */
export function renderRichTextField<Values extends FieldValues>(
    binding: FormFieldBinding<Values>,
    name: Path<Values>,
    {
        icon,
        minRows = 4,
        library,
        findingRefs,
        maxLength = DESCRIPTION_MAX
    }: RichTextFieldOptions = {}
) {
    return (
        <RichTextArea
            control={binding.control}
            name={name}
            label={binding.intl.formatMessage({ id: binding.labelId(name) })}
            icon={icon}
            minRows={minRows}
            maxLength={maxLength}
            library={library}
            findingRefs={findingRefs}
            readOnly={binding.readOnly}
            // Whether the field IS soft-required — the editor watches its own
            // emptiness and draws the hint in its footer.
            recommended={binding.recommended?.(name) ?? false}
        />
    );
}
